import React, { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { ConfirmDialog } from "src/apps/commons/dataDisplays";
import { FormPaper } from "src/apps/commons/papers";
import * as Api from "../api";
import { constants, paths } from "../utils";
import { IRemyndManual, IRemyndManualSaveReqContainer } from "../types";
import Form from "./Form";

interface Props {
  id?: RemyndBrandId;
  isShow: boolean;
}

interface StateTypes {
  fetching: boolean;
  data?: IRemyndManual;
}

interface ConfirmDialogStateTypes {
  open: boolean;
  onConfirm?: () => void;
}

const FormContainers: React.FC<Props> = props => {
  const { id, isShow } = props;
  const router = useRouter();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [state, setState] = useState<StateTypes>({ fetching: true });
  const { fetching, data } = state;

  const [saving, setSaving] = useState(false);
  const [confirmDialogState, setConfirmDialogState] =
    useState<ConfirmDialogStateTypes>({ open: false });
  const handleCloseDialog = () => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  };
  const handleSave = useCallback(
    (req: IRemyndManualSaveReqContainer) => {
      setConfirmDialogState({
        open: true,
        onConfirm: async () => {
          try {
            setSaving(true);
            if (id) {
              await Api.update(id, req);
            } else {
              await Api.save(req);
            }
            await router.push(paths.index);
            setMessage(
              "success",
              `${id ? "แก้ไข" : "สร้าง"} ${constants.label} "${
                req.manual.nameTh
              }" สำเร็จ`
            );
          } catch (e) {
            handleError(e);
            setSaving(false);
            handleCloseDialog();
          }
        }
      });
    },
    [setMessage, handleError, router, id]
  );

  const fetchManual = useCallback(
    async (catLv1Id: RemyndCategoryLv1Id) => {
      setState(d => ({ ...d, fetching: true }));
      try {
        const resp = await Api.get(catLv1Id);
        setState({ fetching: false, data: resp.value });
      } catch (e) {
        handleError(e);
        setState({ fetching: false, data: undefined });
      }
    },
    [handleError]
  );

  useEffect(() => {
    if (id) {
      fetchManual(id);
    } else {
      setState({ fetching: false, data: undefined });
    }
  }, [id, fetchManual]);

  return (
    <FormPaper>
      <Form
        isLoading={fetching}
        isSaving={saving}
        isShow={isShow}
        data={data}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        title={`ยืนยันการ${id ? "แก้ไข" : "สร้าง"}${constants.label}?`}
        open={confirmDialogState.open}
        onClose={handleCloseDialog}
        onConfirm={confirmDialogState.onConfirm}
        confirming={saving}
      />
    </FormPaper>
  );
};

export default FormContainers;
